import { useEffect, useMemo, useState } from 'react';
import {
  ApiErrorText,
  // Button,
  ModalComponentWrapper,
} from '@/shared/components';
// import HistoryIcon from '@/shared/assets/history-icon.svg?react';
import { useWithdrawCryptoForm } from '../model';
import { WithdrawCryptoForm } from './WithdrawCryptoForm';
import { useTranslation } from 'react-i18next';
import { NUMERIC_CONFIG } from '@/shared/config';
import { useModalStore, useOperationsPreSelectAsset } from '@/shared/lib';
import { useWithdrawCryptoModals } from '../lib';
import { WithdrawModalFooter } from '@/entities/operation';
import type { OperationModalProps } from '@/shared/types';
import type { SavedCryptoWalletAddress } from '@/entities/crypto-address';

export const WithdrawCryptoModal = ({
  onBack,
  title,
  onEditSavedCryptoAddress,
  assetCode,
  // onHistoryClick,
}: OperationModalProps & {
  onEditSavedCryptoAddress: (
    savedCryptoAddress: SavedCryptoWalletAddress
  ) => void;
}) => {
  const [isFormDirty, setIsFormDirty] = useState(false);
  const { t } = useTranslation();
  const { setState, setCloseConfirmationRequired } = useModalStore();
  const { openConfirmationModal } = useWithdrawCryptoModals();

  useEffect(() => {
    setCloseConfirmationRequired(isFormDirty);
  }, [isFormDirty, setCloseConfirmationRequired]);

  const {
    form,
    watchedValues,
    totalAmount,
    assetOptions,
    savedCryptoAddresses,
    availableNetworks,
    selectedAsset,
    selectedNetwork,
    minWithdraw,
    hasWithdrawFee,
    isFromDataLoading,
    isFormDataError,
    formDataError,
    commissionData,
    setTemplateId,
    handleFormSubmit,
    error,
    handleMaxClick,
    isLoading,
  } = useWithdrawCryptoForm({
    onValidateSuccess: () => {
      setState('fee', commissionData?.feeAmount || 0);
      openConfirmationModal();
    },
  });

  useOperationsPreSelectAsset({
    assetCode,
    options: assetOptions,
    setValue: code => form.setValue('assetCode', code),
  });

  // const onHistoryClickHandler = () => {
  //   setState('withdrawCryptoFormData', watchedValues);
  //   onHistoryClick(
  //     selectedAsset?.code ? { assetCode: selectedAsset?.code } : undefined
  //   );
  // };

  const handleBack = () => {
    onBack();
    setState('withdrawCryptoFormData', null);
    setState('fee', 0);
  };

  const handleEditSavedCryptoAddress = (
    savedCryptoAddress: SavedCryptoWalletAddress
  ) => {
    setState('withdrawCryptoFormData', watchedValues);
    onEditSavedCryptoAddress(savedCryptoAddress);
  };

  const savedAddressesOptions = useMemo(() => {
    if (!selectedNetwork) return [];

    return savedCryptoAddresses?.filter(
      address => address.networkName === selectedNetwork.name
    );
  }, [selectedNetwork, savedCryptoAddresses]);

  return (
    <ModalComponentWrapper
      title={title}
      onBack={handleBack}
      // actions={
      //   <Button
      //     variant="secondary"
      //     onClick={onHistoryClickHandler}
      //     className="flex items-center justify-center size-[4.2rem] bg-muted hover:bg-muted/80 bg-transparent-bg transition-colors text-foreground shadow-none self-start rounded-full border border-border mr-[0.8rem]"
      //     aria-label="history">
      //     <HistoryIcon className="size-[2.4rem] text-foreground" />
      //   </Button>
      // }
      footerClassName="sm:px-0 px-0 before:invisible"
      footer={
        <WithdrawModalFooter
          precision={
            selectedAsset?.precision || NUMERIC_CONFIG.precision.default
          }
          amount={totalAmount}
          assetCode={watchedValues.assetCode}
          withdrawFee={commissionData?.feeAmount || 0}
          includeFee={hasWithdrawFee && Boolean(watchedValues.includeFee)}
          disabled={isFromDataLoading || isLoading}
          isLoading={isLoading}
          onSubmit={() => form.handleSubmit(handleFormSubmit)()}
          submitLabel={t('withdrawMethods.crypto.submitButton')}
        />
      }>
      <WithdrawCryptoForm
        form={form}
        setTemplateId={setTemplateId}
        watchedValues={watchedValues}
        assetOptions={assetOptions}
        availableNetworks={availableNetworks}
        selectedAsset={selectedAsset}
        selectedNetwork={selectedNetwork}
        minWithdraw={minWithdraw}
        hasWithdrawFee={hasWithdrawFee}
        isLoading={isFromDataLoading}
        isError={isFormDataError}
        error={formDataError}
        onMaxClick={handleMaxClick}
        onFormDirtyChange={setIsFormDirty}
        commissionData={commissionData}
        savedCryptoAddresses={savedAddressesOptions}
        onEditSavedCryptoAddress={handleEditSavedCryptoAddress}
      />
      {!isLoading && error && <ApiErrorText error={error} />}
    </ModalComponentWrapper>
  );
};
